import React, { useState, useContext } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { DollarSign, PieChart } from "lucide-react";
import { ProjectContext } from "./ProjectStructureTab";
import ProjectInfoHeader from "./resource-assignment/ProjectInfoHeader";
import CostBreakdownSection from "./cost-analysis/CostBreakdownSection";
import MarginProjectionsSection from "./cost-analysis/MarginProjectionsSection";
import TaskProfitabilityTable from "./cost-analysis/TaskProfitabilityTable";
import RiskFactorsSection from "./ai-advisor/cost-analysis/RiskFactorsSection";
import { useCostAnalysisOptimized } from "./cost-analysis/hooks/useCostAnalysisOptimized";
import { useTaskProfitability } from "./cost-analysis/hooks/useTaskProfitability";
import { handleDownloadCostAnalysis } from "./cost-analysis/utils/excelExport";
import { formatDuration } from "./cost-analysis/utils/costCalculations";

const CostAnalysisTab = () => {
  const { selectedProject } = useContext(ProjectContext);
  const [profitMargin, setProfitMargin] = useState([30]);
  const [bufferPercent, setBufferPercent] = useState([12.5]);
  const [isTaskProfitabilityOpen, setIsTaskProfitabilityOpen] = useState(true);

  const { costAnalysis, isLoading, resourceAssignments, isAllDataFetched } = useCostAnalysisOptimized(selectedProject);
  const { taskProfitability, projectTaskCostSummary, calculatedTotalCost, roleBasedCosts } = useTaskProfitability(
    resourceAssignments,
    selectedProject,
    profitMargin,
    bufferPercent,
    costAnalysis,
    isAllDataFetched
  );

  const onDownloadCostAnalysis = () => {
    handleDownloadCostAnalysis(
      costAnalysis,
      selectedProject,
      taskProfitability,
      profitMargin,
      bufferPercent,
      calculatedTotalCost,
      resourceAssignments
    );
  };

  if (!selectedProject) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary">
          <CardContent className="p-8 text-center">
            <DollarSign className="h-16 w-16 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Project Selected</h3>
            <p className="text-gray-300">Please select a project from the Project Planning tab to view cost analysis.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading until both cost analysis is loaded AND all resource data is fetched
  const isDataLoading = isLoading || !isAllDataFetched;

  // Check if we have any cost data available - either calculated from resources or stored analysis
  const hasCalculatedData = roleBasedCosts.length > 0 && calculatedTotalCost > 0;
  const hasStoredAnalysis = !!costAnalysis;
  const hasCostData = hasCalculatedData || hasStoredAnalysis;

  console.log('🎯 COST ANALYSIS TAB: Data availability check:', {
    hasCalculatedData,
    hasStoredAnalysis,
    hasCostData,
    roleBasedCostsCount: roleBasedCosts.length,
    calculatedTotalCost,
    costAnalysisExists: !!costAnalysis,
    resourceAssignmentsCount: resourceAssignments.length,
    isAllDataFetched
  });

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <ProjectInfoHeader selectedProject={selectedProject} />

      <div className="flex-grow overflow-auto">
        {isDataLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
            <span className="ml-3 text-white">Loading optimized cost analysis...</span>
            <span className="ml-2 text-xs text-green-300">✅ OPTIMIZED</span>
            {!isAllDataFetched && (
              <span className="ml-2 text-xs text-yellow-300">
                (Fetching resource assignments: {resourceAssignments.length} loaded)
              </span>
            )}
          </div>
        ) : !hasCostData ? (
          <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary">
            <CardContent className="p-8 text-center">
              <PieChart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Cost Analysis Available</h3>
              <p className="text-gray-300">
                {resourceAssignments.length === 0 
                  ? "Assign resources in the Resource Assignment tab or run an AI Project Analysis to generate cost analysis data."
                  : ""
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {/* Cost Breakdown and Margin Projections - Equal Height Sections */}
            <div className="grid grid-cols-2 gap-4">
              <CostBreakdownSection 
                breakdown={roleBasedCosts.length > 0 ? roleBasedCosts : [
                  { category: 'Directors', amount: costAnalysis?.director_amount || 0, description: '', details: '' },
                  { category: 'Managers', amount: costAnalysis?.manager_amount || 0, description: '', details: '' },
                  { category: 'Team Leads', amount: costAnalysis?.teamlead_amount || 0, description: '', details: '' },
                  { category: 'Team Members', amount: costAnalysis?.teammember_amount || 0, description: '', details: '' }
                ]}
                totalCost={calculatedTotalCost || costAnalysis?.total_cost || 0}
                projectTaskCostSummary={projectTaskCostSummary}
              />

              <MarginProjectionsSection
                costAnalysis={costAnalysis}
                selectedProject={selectedProject}
                profitMargin={profitMargin}
                setProfitMargin={setProfitMargin}
                bufferPercent={bufferPercent}
                setBufferPercent={setBufferPercent}
                taskProfitability={taskProfitability}
                calculatedTotalCost={calculatedTotalCost}
                resourceAssignments={resourceAssignments}
              />
            </div>

            <TaskProfitabilityTable
              taskProfitability={taskProfitability}
              isOpen={isTaskProfitabilityOpen}
              onOpenChange={setIsTaskProfitabilityOpen}
              onDownload={onDownloadCostAnalysis}
            />

            <RiskFactorsSection
              riskFactors={costAnalysis?.risk_considerations || []}
              totalCost={calculatedTotalCost || costAnalysis?.total_cost || 0}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CostAnalysisTab;
